/**
 * Exact conversions between what an operator types and what the wire carries
 * (contract §1: money in integer minor units, percent in basis points, never floats).
 *
 * Formatting is not here: `lib/formatMoney.js` is THE formatter. What `src/lib` lacks is
 * an exact PARSER. `admin/money.js#toCents` is `Math.round(Number(x) * 100)`, which maps
 * "abc" to 0 and "12.345" to 1235 without a word; a gift card or a coupon minted from a
 * silently rewritten number is not something the operator can take back (a Stripe coupon
 * cannot be re-priced). So these read the DIGITS of the string and refuse anything that is
 * not a plain decimal with at most two fractional digits: `null` is "not a valid amount",
 * and the form says so instead of sending a number nobody typed.
 */

const DECIMAL_2 = /^(\d{1,9})(?:[.,](\d{1,2}))?$/;

/** Splits "12.5" into [12, 50] (whole, hundredths), or null. Comma accepted for FR/ES input. */
const hundredths = (input) => {
  const m = DECIMAL_2.exec(String(input ?? "").trim());
  if (!m) return null;
  const whole = Number(m[1]);
  const frac = Number((m[2] || "").padEnd(2, "0"));
  return whole * 100 + frac;
};

/** "49.90" -> 4990; "49" -> 4900; "" / "abc" / "1.234" / "-3" -> null. */
export const majorToCents = (input) => hundredths(input);

/** "12.5" (a percentage) -> 1250 basis points; null when not a plain two-decimal number. */
export const percentToBp = (input) => hundredths(input);

/** 1250 -> "12.5", 2000 -> "20": a basis-point value as the percentage an operator reads. */
export const bpToPercent = (bp) => {
  const whole = Math.trunc(bp / 100);
  const frac = String(bp % 100).padStart(2, "0").replace(/0+$/, "");
  return frac ? `${whole}.${frac}` : String(whole);
};
